import { propKind, type LevelProp } from './props';

// Level data as the simulation sees it: a solid-tile bitmap plus spawn points.
// Built from a Tiled JSON map (.tmj) or from an ASCII blockout. Rendering
// details (which tile image goes where) stay in the Tiled JSON; the sim only
// needs to know what blocks movement.

export const TILE_SIZE = 32;

// Tiled layer / object naming conventions used by this project.
export const LAYER_GROUND = 'ground'; // visual only
export const LAYER_WALLS = 'walls'; // any non-empty tile is solid and opaque
export const LAYER_WATER = 'water'; // written by ascii2tmj with solid = true, opaque = false
export const LAYER_CANOPY = 'canopy'; // palm fronds, drawn above the soldiers, never solid
export const LAYER_SPAWNS = 'spawns'; // object layer of point objects
export const OBJECT_SPAWN = 'spawn'; // object class/type
export const LAYER_PROPS = 'props'; // object layer of tile objects whose tile class names a PropKind

// Tiled packs flip/rotate flags into the top bits of a gid.
const GID_MASK = 0x1fffffff;

export interface Spawn {
  x: number;
  y: number;
  team: number; // 0 = any team / deathmatch
}

export interface LevelData {
  readonly name: string;
  // Comfortable player range the level was designed for (Tiled map
  // properties `minPlayers` / `maxPlayers`; defaults 2..20). The room
  // manager pairs room size with level.
  readonly minPlayers: number;
  readonly maxPlayers: number;
  readonly tileSize: number;
  readonly cols: number;
  readonly rows: number;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly solid: Uint8Array; // cols * rows, row-major, 1 = blocks movement
  // 1 = also blocks bullets and line of sight. Walls and rocks are solid and
  // opaque; water is solid only (you cannot wade, but you can be shot across
  // it); grenades fly over everything.
  readonly opaque: Uint8Array;
  readonly spawns: readonly Spawn[];
  // Free-standing objects (palms, huts, crates) for the renderer; their
  // footprints are already baked into solid/opaque. See props.ts.
  readonly props: readonly LevelProp[];
  // Image files the map references (tileset sheets, prop images), relative
  // to the level file, so the client can load them before building the map.
  readonly images: readonly string[];
  // Tiled's map background colour ('#rrggbb'), if set: what shows beyond
  // the tiles (the half-tile strip a dual-grid terrain leaves at the edge).
  readonly backgroundColor?: string;
  // Movement speed multiplier for this level (map property `speedScale`,
  // default 1). Open maps feel faster than cluttered ones at the same
  // px/s; a level can dial itself down. Applied in applyInput on both ends.
  readonly speedScale: number;
}

export function isSolid(level: LevelData, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= level.cols || row >= level.rows) return true;
  return level.solid[row * level.cols + col] === 1;
}

// Blocks bullets and line of sight (walls, rocks, trees; not water).
export function isOpaque(level: LevelData, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= level.cols || row >= level.rows) return true;
  return level.opaque[row * level.cols + col] === 1;
}

// ---- Tiled JSON -----------------------------------------------------------

// The subset of the Tiled JSON map format we read. Full spec:
// https://doc.mapeditor.org/en/stable/reference/json-map-format/
interface TiledProperty {
  name: string;
  value: unknown;
}
export interface TiledObject {
  x: number;
  y: number;
  width?: number;
  height?: number;
  gid?: number; // a tile object: (x, y) is its bottom-left corner
  type?: string; // Tiled < 1.9
  class?: string; // Tiled >= 1.9
  properties?: TiledProperty[];
}
export interface TiledLayer {
  type: 'tilelayer' | 'objectgroup' | 'imagelayer' | 'group';
  name: string;
  data?: number[];
  objects?: TiledObject[];
  layers?: TiledLayer[];
  properties?: TiledProperty[];
  offsetx?: number;
  offsety?: number;
}
export interface TiledTile {
  id: number;
  type?: string; // Tiled < 1.9
  class?: string;
  image?: string; // image-collection tilesets: one image per tile
  imagewidth?: number;
  imageheight?: number;
}
// Tiled terrain (Wang) sets: a corner set labels each tile's four corners
// with a colour. Our convention: colour 1 is the set's lower terrain,
// colour 2 the upper; a colour's custom properties `solid` / `opaque`
// say what its terrain does to movement and bullets.
export interface TiledWangColor {
  name: string;
  properties?: TiledProperty[];
}
export interface TiledWangTile {
  tileid: number;
  wangid: number[]; // top, NE, right, SE, bottom, SW, left, NW; 0 = unset
}
export interface TiledWangSet {
  name: string;
  type?: string;
  colors: TiledWangColor[];
  wangtiles: TiledWangTile[];
}
export interface TiledTileset {
  firstgid: number;
  name: string;
  image?: string; // single-image tilesets
  columns?: number;
  margin?: number; // pixels before the first tile (an extruded sheet has 1)
  spacing?: number; // pixels between tiles (an extruded sheet has 2)
  tiles?: TiledTile[];
  wangsets?: TiledWangSet[];
}
export interface TiledMap {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
  tilesets?: TiledTileset[];
  properties?: TiledProperty[];
  backgroundcolor?: string;
}

function prop(props: TiledProperty[] | undefined, name: string): unknown {
  if (!props) return undefined;
  for (const p of props) if (p.name === name) return p.value;
  return undefined;
}

function* walkLayers(layers: TiledLayer[]): Generator<TiledLayer> {
  for (const l of layers) {
    if (l.type === 'group' && l.layers) yield* walkLayers(l.layers);
    else yield l;
  }
}

// The class of a tile gid ('palm', 'hut', ...) and the images a map uses.
function tileClasses(map: TiledMap): { classOf: Map<number, string>; imageOf: Map<number, string>; images: string[] } {
  const classOf = new Map<number, string>();
  const imageOf = new Map<number, string>();
  const images: string[] = [];
  for (const ts of map.tilesets ?? []) {
    if (ts.image) images.push(ts.image);
    for (const t of ts.tiles ?? []) {
      if (t.image) {
        images.push(t.image);
        imageOf.set(ts.firstgid + t.id, t.image);
      }
      const cls = t.class ?? t.type;
      if (cls) classOf.set(ts.firstgid + t.id, cls);
    }
  }
  return { classOf, imageOf, images };
}

// Dual-grid terrain (the jungle theme, docs/level-design.md): a tile layer
// offset half a tile up-left holds Wang tiles whose four corners sample
// the four cells around the tile's centre, so a cell (c, r) is the SE
// corner of tile (c, r). The cell's terrain is the colour at that corner
// in the top-most dual layer that claims it: a tile whose SE corner is
// the set's upper terrain, or any tile of the bottom layer. (A higher set
// draws its lower colour over whatever is below, which may be a lower
// terrain still, so only an upper corner is trusted above the base.)
function bakeDualGrid(map: TiledMap, layers: TiledLayer[], solid: Uint8Array, opaque: Uint8Array): void {
  const half = map.tilewidth / 2;
  const dual: TiledLayer[] = [];
  for (const l of layers) if (l.type === 'tilelayer' && l.data && l.offsetx === -half && l.offsety === -half) dual.push(l);
  if (dual.length === 0) return;
  const seOf = new Map<number, { colour: number; set: TiledWangSet }>();
  for (const ts of map.tilesets ?? []) {
    for (const set of ts.wangsets ?? []) {
      for (const wt of set.wangtiles) {
        const se = wt.wangid[3] ?? 0;
        if (se !== 0) seOf.set(ts.firstgid + wt.tileid, { colour: se, set });
      }
    }
  }
  const cols = map.width;
  for (let i = 0; i < cols * map.height; i++) {
    for (let k = dual.length - 1; k >= 0; k--) {
      const entry = seOf.get((dual[k]?.data?.[i] ?? 0) & GID_MASK);
      if (!entry || (entry.colour !== 2 && k !== 0)) continue;
      const colour = entry.set.colors[entry.colour - 1];
      if (prop(colour?.properties, 'solid') === true) {
        solid[i] = 1;
        if (prop(colour?.properties, 'opaque') === true) opaque[i] = 1;
      }
      break;
    }
  }
}

export function parseTiledMap(map: TiledMap, name: string): LevelData {
  if (map.tilewidth !== map.tileheight) {
    throw new Error(`level ${name}: tiles must be square`);
  }
  const cols = map.width;
  const rows = map.height;
  const ts = map.tilewidth;
  const solid = new Uint8Array(cols * rows);
  const opaque = new Uint8Array(cols * rows);
  const spawns: Spawn[] = [];
  const props: LevelProp[] = [];
  const { classOf, imageOf, images } = tileClasses(map);

  for (const layer of walkLayers(map.layers)) {
    if (layer.type === 'tilelayer' && layer.data) {
      // The "walls" layer is solid by convention; any other tile layer can
      // opt in with a boolean custom property `solid`. A solid layer also
      // blocks bullets unless it says `opaque = false` (water).
      const layerSolid = layer.name === LAYER_WALLS || prop(layer.properties, 'solid') === true;
      if (!layerSolid) continue;
      const layerOpaque = prop(layer.properties, 'opaque') !== false;
      const n = Math.min(layer.data.length, solid.length);
      for (let i = 0; i < n; i++) {
        if ((layer.data[i]! & GID_MASK) !== 0) {
          solid[i] = 1;
          if (layerOpaque) opaque[i] = 1;
        }
      }
    } else if (layer.type === 'objectgroup' && layer.objects) {
      for (const o of layer.objects) {
        // A spawn is any object with class "spawn", or an untyped object on
        // the "spawns" layer. Objects of other classes are ignored.
        const kind = o.class ?? o.type ?? '';
        const isSpawn = kind === OBJECT_SPAWN || (kind === '' && layer.name === LAYER_SPAWNS);
        if (isSpawn) {
          const team = Number(prop(o.properties, 'team') ?? 0);
          spawns.push({ x: o.x, y: o.y, team: Number.isFinite(team) ? team : 0 });
          continue;
        }
        // A prop: a tile object whose tile class names a kind (Tiled draws
        // the tile image; its (x, y) is the bottom-left corner), or a point
        // object with the kind as its own class. The footprint, centred on
        // the image centre, is baked into the solid/opaque bitmaps; cells
        // whose centre falls inside it are blocked.
        const propName = o.gid !== undefined ? classOf.get(o.gid & GID_MASK) : kind;
        const pk = propName ? propKind(propName) : undefined;
        if (!pk) continue;
        const cx = o.gid !== undefined ? o.x + (o.width ?? 0) / 2 : o.x;
        const cy = o.gid !== undefined ? o.y - (o.height ?? 0) / 2 : o.y;
        props.push({ kind: pk.name, x: cx, y: cy, image: o.gid !== undefined ? imageOf.get(o.gid & GID_MASK) : undefined });
        if (pk.footW === 0 || pk.footH === 0) continue;
        const c0 = Math.round(cx / ts - pk.footW / 2);
        const r0 = Math.round(cy / ts - pk.footH / 2);
        for (let r = r0; r < r0 + pk.footH; r++) {
          for (let c = c0; c < c0 + pk.footW; c++) {
            if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
            solid[r * cols + c] = 1;
            if (pk.opaque) opaque[r * cols + c] = 1;
          }
        }
      }
    }
  }

  bakeDualGrid(map, [...walkLayers(map.layers)], solid, opaque);

  const minP = Number(prop(map.properties, 'minPlayers') ?? 2);
  const maxP = Number(prop(map.properties, 'maxPlayers') ?? 20);
  const speed = Number(prop(map.properties, 'speedScale') ?? 1);
  return {
    name,
    minPlayers: Number.isFinite(minP) && minP >= 1 ? minP : 2,
    maxPlayers: Number.isFinite(maxP) && maxP >= minP ? maxP : 20,
    tileSize: map.tilewidth,
    cols,
    rows,
    widthPx: cols * map.tilewidth,
    heightPx: rows * map.tileheight,
    solid,
    opaque,
    spawns,
    props,
    images,
    backgroundColor: map.backgroundcolor,
    speedScale: Number.isFinite(speed) && speed > 0 ? speed : 1,
  };
}

// ---- ASCII blockouts ------------------------------------------------------
//
//   #  wall   o  rock   T  palm tree        solid and opaque
//   ~  water                                solid, bullets fly over
//   .  grass  ,  sand   :  path  *  flowers  =  bridge  v  hole   floor
//   S  spawn (any)   1-9  spawn for team N
//
// Used by tests and by tools/ascii2tmj (which also paints them). Rows may
// differ in length; short rows are padded with floor.

export const ASCII_WALL = '#';
export const ASCII_ROCK = 'o';
export const ASCII_TREE = 'T';
export const ASCII_WATER = '~';
export const ASCII_FLOOR = '.';
export const ASCII_SAND = ',';
export const ASCII_PATH = ':';
export const ASCII_FLOWERS = '*';
export const ASCII_BRIDGE = '=';
export const ASCII_HOLE = 'v';
export const ASCII_SPAWN = 'S';

export function levelFromAscii(text: string, name = 'ascii', tileSize = TILE_SIZE): LevelData {
  const lines = text.replace(/\r/g, '').split('\n').filter((l, i, arr) => !(l === '' && i === arr.length - 1));
  const rows = lines.length;
  let cols = 0;
  for (const l of lines) if (l.length > cols) cols = l.length;
  const solid = new Uint8Array(cols * rows);
  const opaque = new Uint8Array(cols * rows);
  const spawns: Spawn[] = [];

  for (let r = 0; r < rows; r++) {
    const line = lines[r]!;
    for (let c = 0; c < cols; c++) {
      const ch = c < line.length ? line[c]! : ASCII_FLOOR;
      if (ch === ASCII_WALL || ch === ASCII_ROCK || ch === ASCII_TREE) {
        solid[r * cols + c] = 1;
        opaque[r * cols + c] = 1;
      } else if (ch === ASCII_WATER) {
        solid[r * cols + c] = 1;
      } else if (ch === ASCII_SPAWN || (ch >= '1' && ch <= '9')) {
        spawns.push({
          x: (c + 0.5) * tileSize,
          y: (r + 0.5) * tileSize,
          team: ch === ASCII_SPAWN ? 0 : ch.charCodeAt(0) - 48,
        });
      }
    }
  }

  return {
    minPlayers: 2,
    maxPlayers: 20,
    name,
    tileSize,
    cols,
    rows,
    widthPx: cols * tileSize,
    heightPx: rows * tileSize,
    solid,
    opaque,
    spawns,
    props: [],
    images: [],
    speedScale: 1,
  };
}
